/** Data hooks for Phase 1 objects: accounts, deals, pipelines, activities, timeline, import, search. */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, qs, tokenStore } from "./client";
import type {
  Account, Activity, ActivityKind, Board, Deal, ImportObject, ImportResult, ImportSource, Page, Pipeline,
  SearchHit, StageKind, TimelineItem,
} from "./types";

type Body = Record<string, unknown>;

export const crmKeys = {
  accounts: (q: string, type: string) => ["accounts", q, type] as const,
  account: (id: string) => ["account", id] as const,
  deals: (filters: Body) => ["deals", filters] as const,
  deal: (id: string) => ["deal", id] as const,
  board: (pipelineId: string | null, q: string) => ["board", pipelineId, q] as const,
  pipelines: ["pipelines"] as const,
  activities: (filters: Body) => ["activities", filters] as const,
  timeline: (type: string, id: string) => ["timeline", type, id] as const,
};

function useInvalidate() {
  const qc = useQueryClient();
  return (...prefixes: string[]) => prefixes.forEach((p) => void qc.invalidateQueries({ queryKey: [p] }));
}

// ---- accounts ----
export const useAccounts = (q: string, type = "", territory = "") =>
  useInfiniteQuery({
    queryKey: [...crmKeys.accounts(q, type), territory],
    queryFn: ({ pageParam }) =>
      api<Page<Account>>("GET", `/accounts${qs({ q, account_type: type, territory_id: territory, cursor: pageParam, limit: 25 })}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });

export const useAccount = (id: string) =>
  useQuery({ queryKey: crmKeys.account(id), queryFn: () => api<Account>("GET", `/accounts/${id}`) });

export function useSaveAccount(id?: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (data: Body) => (id ? api<Account>("PATCH", `/accounts/${id}`, data) : api<Account>("POST", "/accounts", data)),
    onSuccess: (a) => {
      qc.setQueryData(crmKeys.account(a.id), a);
      invalidate("accounts", "timeline", "contacts", "deals", "board");
    },
  });
}

export function useDeleteAccount() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: string) => api<void>("DELETE", `/accounts/${id}`), onSuccess: () => invalidate("accounts") });
}

export const useContactsOf = (accountId: string) =>
  useQuery({
    queryKey: ["contacts", "account", accountId],
    queryFn: () => api<Page<import("./types").Contact>>("GET", `/contacts${qs({ account_id: accountId, limit: 100 })}`),
  });

// ---- pipelines and deals ----
export const usePipelines = () =>
  useQuery({ queryKey: crmKeys.pipelines, queryFn: () => api<Pipeline[]>("GET", "/pipelines"), staleTime: 60_000 });

export function useSavePipeline(id?: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (data: { name: string; is_default: boolean; stages: { id?: string; name: string; probability: number; kind: StageKind }[] }) =>
      id ? api<Pipeline>("PUT", `/pipelines/${id}`, data) : api<Pipeline>("POST", "/pipelines", data),
    onSuccess: () => invalidate("pipelines", "board", "deals", "deal"),
  });
}

export const useBoard = (pipelineId: string | null, q: string) =>
  useQuery({
    queryKey: crmKeys.board(pipelineId, q),
    queryFn: () => api<Board>("GET", `/deals/board${qs({ pipeline_id: pipelineId, q })}`),
    placeholderData: keepPreviousData,
  });

export const useDeals = (filters: Body, enabled = true) =>
  useQuery({
    queryKey: crmKeys.deals(filters),
    queryFn: () => api<Page<Deal>>("GET", `/deals${qs({ ...filters, limit: 100 })}`),
    enabled,
  });

export const useDeal = (id: string) =>
  useQuery({ queryKey: crmKeys.deal(id), queryFn: () => api<Deal>("GET", `/deals/${id}`) });

export function useSaveDeal(id?: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (data: Body) => (id ? api<Deal>("PATCH", `/deals/${id}`, data) : api<Deal>("POST", "/deals", data)),
    onSuccess: (d) => {
      qc.setQueryData(crmKeys.deal(d.id), d);
      invalidate("board", "deals", "timeline", "activities");
    },
  });
}

/** Move a deal on the board with an optimistic update; rolls back on error. */
export function useMoveDeal(boardKey: readonly unknown[]) {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ deal, stageId, lostReason }: { deal: Deal; stageId: string; lostReason?: string }) =>
      api<Deal>("PATCH", `/deals/${deal.id}`, { stage_id: stageId, ...(lostReason ? { lost_reason: lostReason } : {}) }),
    onMutate: async ({ deal, stageId }) => {
      await qc.cancelQueries({ queryKey: boardKey });
      const previous = qc.getQueryData<Board>(boardKey);
      if (previous) {
        qc.setQueryData<Board>(boardKey, {
          ...previous,
          columns: previous.columns.map((c) => ({
            ...c,
            deals: c.stage.id === stageId ? [{ ...deal }, ...c.deals.filter((d) => d.id !== deal.id)] : c.deals.filter((d) => d.id !== deal.id),
          })),
        });
      }
      return { previous };
    },
    onError: (_e, _v, ctx) => { if (ctx?.previous) qc.setQueryData(boardKey, ctx.previous); },
    onSettled: () => invalidate("board", "deals", "deal", "timeline"),
  });
}

export function useDeleteDeal() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: string) => api<void>("DELETE", `/deals/${id}`), onSuccess: () => invalidate("board", "deals") });
}

// ---- activities and timeline ----
export const useActivities = (filters: { kind?: ActivityKind; status?: string; mine?: boolean }) =>
  useQuery({
    queryKey: crmKeys.activities(filters),
    queryFn: () => api<Page<Activity>>("GET", `/activities${qs({ ...filters, mine: filters.mine ? "true" : undefined, limit: 100 })}`),
  });

export function useCreateActivity() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (data: Body) => api<Activity>("POST", "/activities", data), onSuccess: () => invalidate("activities", "timeline") });
}

export function useUpdateActivity() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...data }: Body & { id: string }) => api<Activity>("PATCH", `/activities/${id}`, data),
    onSuccess: () => invalidate("activities", "timeline"),
  });
}

export const useTimeline = (type: "contact" | "account" | "deal", id: string) =>
  useQuery({ queryKey: crmKeys.timeline(type, id), queryFn: () => api<TimelineItem[]>("GET", `/timeline/${type}/${id}`) });

// ---- import, export, search ----
export function useImport() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ object, csv, dryRun, pipelineId, source = "generic", stageMap = {} }: {
      object: ImportObject; csv: string; dryRun: boolean; pipelineId?: string; source?: ImportSource; stageMap?: Record<string, string>;
    }) =>
      api<ImportResult>("POST", `/imports/${object}`, { csv, dry_run: dryRun, pipeline_id: pipelineId || null, source, stage_map: stageMap }),
    onSuccess: (r) => { if (!r.dry_run) invalidate("contacts", "accounts", "deals", "board", "products", "picker", "dashboard"); },
  });
}

/** Download a CSV export using the in-memory access token. */
export async function downloadExport(object: ImportObject): Promise<void> {
  const res = await fetch(`/api/v1/exports/${object}`, { headers: { Authorization: `Bearer ${tokenStore.get() ?? ""}` } });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? `${object}s.csv`;
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const useSearch = (q: string) =>
  useQuery({
    queryKey: ["search", q],
    queryFn: () => api<SearchHit[]>("GET", `/search${qs({ q })}`),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
  });
